// Stripe.js, loaded from Stripe's CDN with a script tag (Stripe requires js.stripe.com; there is no
// npm dependency). Only the slice of the API the signup card step uses is typed here.
//
//   const stripe = await loadStripe(publishableKey);
//   const elements = stripe.elements({ clientSecret, appearance: stripeAppearance(theme), fonts: STRIPE_FONTS });
//   elements.create("payment").mount(el);
//   await stripe.confirmPayment({ elements, redirect: "if_required", confirmParams: { return_url } });

export const STRIPE_JS_URL = "https://js.stripe.com/v3/";

export interface StripeError {
  /** card_error | validation_error | invalid_request_error | api_error | … */
  type: string;
  code?: string;
  decline_code?: string;
  message?: string;
}

export interface StripePaymentIntent {
  id: string;
  /** requires_payment_method | requires_confirmation | requires_action | processing | requires_capture | canceled | succeeded */
  status: string;
}

export interface StripeConfirmResult {
  error?: StripeError;
  paymentIntent?: StripePaymentIntent;
}

/** https://docs.stripe.com/elements/appearance-api */
export interface StripeAppearance {
  theme?: "stripe" | "night" | "flat";
  labels?: "above" | "floating";
  variables?: Record<string, string>;
  rules?: Record<string, Record<string, string>>;
}

export interface StripeFont {
  cssSrc: string;
}

export interface StripeElementsOptions {
  clientSecret: string;
  appearance?: StripeAppearance;
  fonts?: StripeFont[];
  loader?: "auto" | "always" | "never";
}

export interface StripePaymentElementOptions {
  layout?: "tabs" | "accordion" | { type: "tabs" | "accordion"; defaultCollapsed?: boolean };
  defaultValues?: { billingDetails?: { name?: string; email?: string } };
  business?: { name: string };
  wallets?: { applePay?: "auto" | "never"; googlePay?: "auto" | "never"; link?: "auto" | "never" };
}

export interface StripePaymentElementChange {
  complete: boolean;
  empty: boolean;
}

export interface StripePaymentElement {
  mount(target: HTMLElement | string): void;
  destroy(): void;
  on(event: "ready", handler: () => void): StripePaymentElement;
  on(event: "change", handler: (e: StripePaymentElementChange) => void): StripePaymentElement;
  on(event: "loaderror", handler: (e: { error?: StripeError }) => void): StripePaymentElement;
}

export interface StripeElements {
  create(type: "payment", options?: StripePaymentElementOptions): StripePaymentElement;
  update(options: { appearance?: StripeAppearance }): void;
}

export interface StripeClient {
  initEmbeddedCheckout(options: { clientSecret: string; onComplete: () => void }): Promise<StripeEmbeddedCheckout>;
  elements(options: StripeElementsOptions): StripeElements;
  /** Read a PaymentIntent's status with its client secret (publishable key only). */
  retrievePaymentIntent(clientSecret: string): Promise<StripeConfirmResult>;
  confirmPayment(options: {
    elements: StripeElements;
    redirect: "if_required";
    confirmParams: { return_url: string };
  }): Promise<StripeConfirmResult>;
}

export interface StripeEmbeddedCheckout {
  mount(target: HTMLElement | string): void;
  destroy(): void;
}

type StripeFactory = (publishableKey: string) => StripeClient;

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

const LOAD_TIMEOUT_MS = 20_000;
let scriptPromise: Promise<StripeFactory> | null = null;
const clients = new Map<string, StripeClient>();

function loadScript(): Promise<StripeFactory> {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (scriptPromise) return scriptPromise;
  const attempt = new Promise<StripeFactory>((resolve, reject) => {
    // A tag left over from a failed attempt never fires load again: start clean.
    document.querySelectorAll(`script[src="${STRIPE_JS_URL}"]`).forEach((s) => s.remove());
    const script = document.createElement("script");
    script.src = STRIPE_JS_URL;
    script.async = true;
    const timer = window.setTimeout(() => fail(), LOAD_TIMEOUT_MS);
    function fail() {
      window.clearTimeout(timer);
      script.remove();
      reject(new Error("stripe_js_unavailable"));
    }
    script.addEventListener("load", () => {
      window.clearTimeout(timer);
      if (window.Stripe) resolve(window.Stripe);
      else fail();
    });
    script.addEventListener("error", fail);
    document.head.appendChild(script);
  });
  // A failed load (offline, a blocker) can be retried.
  scriptPromise = attempt.catch((err: unknown) => {
    scriptPromise = null;
    throw err;
  });
  return scriptPromise;
}

/** Stripe.js for this publishable key (one client per key). Rejects when the script can't load. */
export async function loadStripe(publishableKey: string): Promise<StripeClient> {
  const cached = clients.get(publishableKey);
  if (cached) return cached;
  const factory = await loadScript();
  const client = factory(publishableKey);
  clients.set(publishableKey, client);
  return client;
}

// ── Appearance: the Payment Element lives in Stripe's iframe, so it can't read our CSS variables.
// Read the resolved brand tokens and hand Stripe literal values (dark and light).

export const STRIPE_FONTS: StripeFont[] = [{ cssSrc: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap" }];

type Theme = "light" | "dark";

const TOKEN_FALLBACK: Record<Theme, Record<string, string>> = {
  dark: {
    "--gf-field-bg": "#0d0816",
    "--gf-surface": "#151021",
    "--gf-surface-2": "#1c1430",
    "--gf-text": "#f4edf3",
    "--gf-text-2": "#b3a7bf",
    "--gf-muted": "#867b90",
    "--gf-faint": "#5f5569",
    "--gf-border-strong": "rgba(214, 120, 180, 0.22)",
    "--gf-accent": "#d63f78",
    "--gf-accent-bright": "#f581ab",
    "--gf-danger": "#f04f6a",
    "--gf-danger-text": "#ff8fa1",
    "--gf-success": "#3ccf8e",
  },
  light: {
    "--gf-field-bg": "#fefcfd",
    "--gf-surface": "#ffffff",
    "--gf-surface-2": "#f8f4f7",
    "--gf-text": "#1f1426",
    "--gf-text-2": "#4b4056",
    "--gf-muted": "#685d72",
    "--gf-faint": "#9a90a3",
    "--gf-border-strong": "rgba(96, 42, 86, 0.24)",
    "--gf-accent": "#c02c68",
    "--gf-accent-bright": "#a82259",
    "--gf-danger": "#cc2944",
    "--gf-danger-text": "#b4223b",
    "--gf-success": "#11845a",
  },
};

function readToken(theme: Theme, name: string): string {
  let value = "";
  try {
    value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  } catch {
    value = "";
  }
  return value || TOKEN_FALLBACK[theme][name] || "";
}

/** The Payment Element styled like a Grow My X form: Manrope, brand inputs, pink focus. */
export function stripeAppearance(theme: Theme): StripeAppearance {
  const t = (name: string) => readToken(theme, name);
  const accent = t("--gf-accent");
  const border = t("--gf-border-strong");
  return {
    theme: theme === "dark" ? "night" : "stripe",
    labels: "above",
    variables: {
      colorPrimary: accent,
      colorBackground: t("--gf-field-bg"),
      colorText: t("--gf-text"),
      colorTextSecondary: t("--gf-text-2"),
      colorTextPlaceholder: t("--gf-faint"),
      colorIcon: t("--gf-muted"),
      colorDanger: t("--gf-danger"),
      colorSuccess: t("--gf-success"),
      fontFamily: 'Manrope, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      fontSizeBase: "15px",
      fontWeightNormal: "500",
      borderRadius: "6px",
      spacingUnit: "4px",
    },
    rules: {
      ".Input": { border: `1px solid ${border}`, boxShadow: "none" },
      ".Input:hover": { border: `1px solid ${t("--gf-muted")}` },
      ".Input:focus": { border: `1px solid ${accent}`, boxShadow: `0 0 0 1px ${accent}` },
      ".Input--invalid": { border: `1px solid ${t("--gf-danger")}`, boxShadow: "none" },
      ".Label": { color: t("--gf-text-2"), fontWeight: "600", fontSize: "13px" },
      ".Tab": { backgroundColor: t("--gf-surface"), border: `1px solid ${border}`, boxShadow: "none" },
      ".Tab:hover": { color: t("--gf-text") },
      ".Tab--selected": { backgroundColor: t("--gf-surface-2"), border: `1px solid ${accent}`, boxShadow: `0 0 0 1px ${accent}` },
      ".Error": { color: t("--gf-danger-text") },
    },
  };
}
